/**
 * The CRM's one-of-N dropdowns, on Radix.
 *
 * Everything in this app that used to be a native `<select>` is one of the three
 * dresses below. They differ only in the pill the trigger wears: `SelectField`
 * is the raised 44px toolbar filter, `SelectPill` the 44px settings field, and
 * `SelectInput` the 52px sunken pill that sits in a dialog beside `crm-inp`
 * text boxes.
 *
 * ── WHY NOT THE NATIVE CONTROL ──────────────────────────────────────────────
 *
 * A native select inside a `.crm-inp` pill draws no arrow at all, because that
 * rule sets `appearance: none` and nothing was ever put back. So a dropdown
 * holding one word was indistinguishable from a text box holding one word - and
 * when it did open, it dropped the operating system's grey list on top of the
 * app's own surfaces. Both halves were reported from the invoice payment dialog.
 *
 * ── THREE THINGS THIS FILE OWNS SO NO CALL SITE HAS TO ──────────────────────
 *
 * 1. `<option>` CHILDREN. Call sites keep writing plain options, which is what
 *    every one of them already had, and the mapping to `SelectItem` happens
 *    here. A sweep that also rewrote thirty option lists by hand would have been
 *    thirty chances to drop one.
 *
 * 2. THE EMPTY VALUE. Radix throws on `<Select.Item value="">`, and at least six
 *    screens use exactly that for "unassigned", "no parent", "no task". The
 *    empty string is swapped for a sentinel on the way in and swapped back on
 *    the way out, so a caller still reads and writes `""`.
 *
 * 3. THE STACKING ORDER. `.crm-scrim` is `z-index: 80` and the shared
 *    `SelectContent` is `z-[80]` too. Equal values leave the winner to paint
 *    order, which is not something to rely on, so the panel is lifted to 90.
 *    `crm/ui/Modal` already stands its Escape handler down while a Radix popper
 *    is open, so the two nest correctly in every other respect.
 */
import { Children, Fragment, isValidElement, type ReactElement, type ReactNode } from "react";
import { Select, SelectContent, SelectItem, SelectTriggerBare, SelectValue, cn } from "@wa/ui";

/**
 * Stands in for the empty string, which Radix forbids on an item.
 *
 * A control character, so it can never collide with a real value: no screen
 * stores one, and none could be typed into the fields that feed these lists.
 */
const EMPTY = "\u0000empty";

const toRadix = (value: string) => (value === "" ? EMPTY : value);
const fromRadix = (value: string) => (value === EMPTY ? "" : value);

/**
 * `<option>` children, as Radix items.
 *
 * Fragments are walked into rather than skipped: `{cond ? <><option/><option/></>
 * : null}` is ordinary JSX and `Children.toArray` does not flatten it, so a
 * shim that only looked one level deep would silently drop those options.
 * Anything that is not an option is dropped, which is the honest answer for a
 * control whose whole content is its list.
 */
function toItems(children: ReactNode): ReactElement[] {
  const out: ReactElement[] = [];
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    if (child.type === Fragment) {
      out.push(...toItems((child.props as { children?: ReactNode }).children));
      continue;
    }
    if (child.type !== "option") continue;
    const props = child.props as { value?: string | number; children?: ReactNode };
    const value = toRadix(String(props.value ?? ""));
    out.push(
      <SelectItem key={value} value={value}>
        {props.children}
      </SelectItem>,
    );
  }
  return out;
}

/**
 * What every dress needs on top of its pill.
 *
 * The pill classes (`crm-inp`, `crm-fld`) are already flex rows with their own
 * height, padding, radius and background, and `SelectTriggerBare` brings no look
 * of its own, so nothing here has to undo anything - which is the whole reason
 * that bare trigger exists. See `packages/ui/src/components/select.tsx`.
 *
 * `[&>span]` reaches the value and only the value: the chevron beside it is
 * rendered `asChild`, so it is an `svg` rather than a span. Truncating there
 * rather than on the button keeps a long option from widening the field.
 */
const VALUE = "justify-between [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate [&>span]:text-left";

interface SelectMenuProps {
  value: string;
  onChange: (value: string) => void;
  /** The accessible name. The trigger only ever shows the current option. */
  label?: string;
  disabled?: boolean;
  /** Extra classes for the trigger - width, a grid span, a tighter height. */
  className?: string;
  /** Plain `<option>` elements. See `toItems`. */
  children: ReactNode;
}

/** The shared core. Every dress below is this with a different pill. */
function SelectMenu({
  value,
  onChange,
  label,
  disabled,
  dress,
  className,
  children,
}: SelectMenuProps & { dress: string }) {
  return (
    <Select value={toRadix(value)} onValueChange={(next) => onChange(fromRadix(next))} disabled={disabled}>
      <SelectTriggerBare className={cn(VALUE, dress, className)} aria-label={label} title={label}>
        <SelectValue />
      </SelectTriggerBare>
      {/* Above `.crm-scrim`, and matched to the trigger so a long option cannot
          make the panel narrower than the field it belongs to. */}
      <SelectContent className="z-[90] min-w-[var(--radix-select-trigger-width)]">{toItems(children)}</SelectContent>
    </Select>
  );
}

/** The raised filter pill that stands on the page surface, in a toolbar. */
export function SelectField(props: SelectMenuProps) {
  return <SelectMenu {...props} dress="crm-fld crm-fld-sel" />;
}

/** The settings panel's field: the sunken pill, at the shorter 44px height. */
export function SelectPill(props: SelectMenuProps) {
  return <SelectMenu {...props} dress="crm-inp h-11 w-full" />;
}

/** The 52px sunken pill a dialog's fields wear, beside its `crm-inp` text boxes. */
export function SelectInput(props: SelectMenuProps) {
  return <SelectMenu {...props} dress="crm-inp w-full" />;
}
